import React, { useEffect, useMemo, useState, Component } from 'react';
import { Plus, Edit2, Trash2, Tag, Percent, DollarSign, Gift, ToggleLeft, ToggleRight, Loader2, Calendar, ShoppingBag, AlertCircle, Search, Filter, Check, Building2, Utensils } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useToast } from '../components/ui/Toast';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getSupabase } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { getCompanies } from '../utils/companyManagement';
interface Promotion {
  id: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed' | 'bogo' | 'free_item';
  value: number;
  code?: string;
  minOrderAmount: number;
  maxDiscount: number;
  validFrom: string;
  validTo: string;
  active: boolean;
  usageLimit: number;
  usageCount: number;
  applicableCompanies: any[];
  applicableMenuItems: any[];
  createdBy: string;
  createdAt?: string;
}
export function PromotionsPage() {
  useDocumentTitle('Promociones');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Promotion | null>(null);
  const [loading, setLoading] = useState(true);
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [typeFilter, setTypeFilter] = useState('Todos');
  const {
    showToast
  } = useToast();
  const {
    addLog
  } = useActivityLog();
  useEffect(() => {
    loadData();
  }, []);
  // Real-time subscription
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('promotions-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'promotions'
    }, () => {
      loadPromotionsSilent();
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPromotions(), loadCompaniesAndMenu()]);
    } finally {
      setLoading(false);
    }
  };
  const loadCompaniesAndMenu = async () => {
    try {
      // Load companies
      if (isSupabaseConfigured()) {
        const dbCompanies = await supabaseClient.getCompanies();
        setCompanies(dbCompanies);
      } else {
        setCompanies(getCompanies());
      }
      // Load menu items
      if (isSupabaseConfigured()) {
        const items = await supabaseClient.getMenuItems();
        setMenuItems(items);
      }
    } catch (error) {
      console.error('Error loading auxiliary data:', error);
    }
  };
  const loadPromotionsSilent = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getPromotions();
        setPromotions(data);
      }
    } catch (error) {
      console.error('Error auto-refreshing promotions:', error);
    }
  };
  const loadPromotions = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getPromotions();
        setPromotions(data);
      } else {
        const saved = localStorage.getItem('criket_promotions');
        setPromotions(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading promotions:', error);
      const saved = localStorage.getItem('criket_promotions');
      setPromotions(saved ? JSON.parse(saved) : []);
    }
  };
  const handleSave = async (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => {
    try {
      if (editingPromotion) {
        if (isSupabaseConfigured()) {
          await supabaseClient.updatePromotion(editingPromotion.id, promotion);
        }
        setPromotions(promotions.map(p => p.id === editingPromotion.id ? {
          ...p,
          ...promotion
        } : p));
        addLog('Promoción Actualizada', promotion.name, 'promotion');
        showToast('success', 'Promoción actualizada', `"${promotion.name}" ha sido actualizada`);
      } else {
        const newPromotion = {
          ...promotion,
          id: `promo-${Date.now()}`,
          usageCount: 0
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.createPromotion(newPromotion);
        }
        setPromotions([newPromotion, ...promotions]);
        addLog('Promoción Creada', promotion.name, 'promotion');
        showToast('success', 'Promoción creada', `"${promotion.name}" ha sido creada`);
      }
      await loadPromotions();
      setShowModal(false);
      setEditingPromotion(null);
    } catch (error) {
      console.error('Error saving promotion:', error);
      showToast('error', 'Error', 'No se pudo guardar la promoción');
    }
  };
  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deletePromotion(deleteConfirm.id);
        }
        setPromotions(promotions.filter(p => p.id !== deleteConfirm.id));
        addLog('Promoción Eliminada', deleteConfirm.name, 'promotion');
        showToast('success', 'Promoción eliminada', `"${deleteConfirm.name}" ha sido eliminada`);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting promotion:', error);
        showToast('error', 'Error', 'No se pudo eliminar la promoción');
      }
    }
  };
  const toggleActive = async (promotion: Promotion) => {
    try {
      const newActive = !promotion.active;
      if (isSupabaseConfigured()) {
        await supabaseClient.updatePromotion(promotion.id, {
          active: newActive
        });
      }
      setPromotions(promotions.map(p => p.id === promotion.id ? {
        ...p,
        active: newActive
      } : p));
      showToast('success', newActive ? 'Promoción activada' : 'Promoción desactivada');
    } catch (error) {
      console.error('Error toggling promotion:', error);
      showToast('error', 'Error', 'No se pudo cambiar el estado');
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent size={20} data-id="element-5347" />;
      case 'fixed':
        return <DollarSign size={20} data-id="element-5348" />;
      case 'bogo':
        return <Gift size={20} data-id="element-5349" />;
      case 'free_item':
        return <Gift size={20} data-id="element-5350" />;
      default:
        return <Tag size={20} data-id="element-5351" />;
    }
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'percentage':
        return 'Porcentaje';
      case 'fixed':
        return 'Monto Fijo';
      case 'bogo':
        return 'Compra 1 Lleva 2';
      case 'free_item':
        return 'Artículo Gratis';
      default:
        return type;
    }
  };
  const getStatus = (promotion: Promotion) => {
    const now = new Date();
    const validFrom = new Date(promotion.validFrom);
    const validTo = new Date(promotion.validTo);
    // Adjust validTo to end of day
    validTo.setHours(23, 59, 59, 999);
    if (!promotion.active) return 'inactive';
    if (now < validFrom) return 'scheduled';
    if (now > validTo) return 'expired';
    if (promotion.usageLimit > 0 && promotion.usageCount >= promotion.usageLimit) return 'depleted';
    return 'active';
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800" data-id="element-5352">
            Activa
          </span>;
      case 'inactive':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700" data-id="element-5353">
            Inactiva
          </span>;
      case 'scheduled':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800" data-id="element-5354">
            Programada
          </span>;
      case 'expired':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800" data-id="element-5355">
            Expirada
          </span>;
      case 'depleted':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800" data-id="element-5356">
            Agotada
          </span>;
      default:
        return null;
    }
  };
  const filteredPromotions = useMemo(() => {
    return promotions.filter(p => {
      // Search filter
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.code?.toLowerCase().includes(searchTerm.toLowerCase()) || p.description.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      // Status filter
      const status = getStatus(p);
      if (statusFilter !== 'Todas') {
        if (statusFilter === 'Activas' && status !== 'active') return false;
        if (statusFilter === 'Inactivas' && status !== 'inactive') return false;
        if (statusFilter === 'Expiradas' && status !== 'expired') return false;
        if (statusFilter === 'Programadas' && status !== 'scheduled') return false;
      }
      // Type filter
      if (typeFilter !== 'Todos') {
        const typeMap: Record<string, string> = {
          Porcentaje: 'percentage',
          'Monto Fijo': 'fixed',
          '2x1': 'bogo',
          Gratis: 'free_item'
        };
        if (p.type !== typeMap[typeFilter]) return false;
      }
      return true;
    });
  }, [promotions, searchTerm, statusFilter, typeFilter]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5357">
        <div className="text-center" data-id="element-5358">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto mb-4" data-id="element-5359" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-5360">
            Cargando promociones...
          </p>
        </div>
      </div>;
  }
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-5361">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5362">
        <div data-id="element-5363">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight mb-2" data-id="element-5364">
            Gestión de Promociones
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg" data-id="element-5365">
            Crea y administra descuentos y ofertas especiales
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-5367" />} onClick={() => {
        setEditingPromotion(null);
        setShowModal(true);
      }} size="lg" className="shadow-lg shadow-gray-900/10" data-id="element-5366">
          Nueva Promoción
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5368">
        <div className="flex-1" data-id="element-5369">
          <SearchBar placeholder="Buscar por nombre, código..." value={searchTerm} onSearch={setSearchTerm} className="w-full" data-id="element-5370" />
        </div>
        <div className="flex gap-3" data-id="element-5371">
          <FilterDropdown label="Estado" value={statusFilter} options={['Todas', 'Activas', 'Inactivas', 'Expiradas', 'Programadas']} onChange={setStatusFilter} className="w-40" data-id="element-5372" />
          <FilterDropdown label="Tipo" value={typeFilter} options={['Todos', 'Porcentaje', 'Monto Fijo', '2x1', 'Gratis']} onChange={setTypeFilter} className="w-40" data-id="element-5373" />
        </div>
      </div>

      {/* Promotions List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5374">
        {filteredPromotions.length === 0 ? <div className="col-span-1 lg:col-span-2 text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800" data-id="element-5375">
            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5376">
              <Tag size={32} className="text-gray-400" data-id="element-5377" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5378">
              No se encontraron promociones
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2 mb-6" data-id="element-5379">
              Intenta ajustar los filtros de búsqueda.
            </p>
            {promotions.length === 0 && <Button onClick={() => setShowModal(true)} leftIcon={<Plus size={18} data-id="element-5381" />} data-id="element-5380">
                Crear Primera Promoción
              </Button>}
          </div> : filteredPromotions.map(promotion => {
        const status = getStatus(promotion);
        const usagePercent = promotion.usageLimit > 0 ? Math.min(100, promotion.usageCount / promotion.usageLimit * 100) : 0;
        return <Card key={promotion.id} className="hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-800 group relative overflow-hidden" data-id="element-5382">
                {/* Status Stripe */}
                <div className={`absolute top-0 left-0 w-1.5 h-full ${status === 'active' ? 'bg-green-500' : status === 'expired' ? 'bg-red-500' : status === 'scheduled' ? 'bg-blue-500' : 'bg-gray-300'}`} data-id="element-5383" />

                <div className="pl-3" data-id="element-5384">
                  <div className="flex items-start justify-between mb-4" data-id="element-5385">
                    <div className="flex items-start gap-4" data-id="element-5386">
                      <div className={`p-3 rounded-xl border ${promotion.active ? 'bg-black text-white border-black' : 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`} data-id="element-5387">
                        {getTypeIcon(promotion.type)}
                      </div>
                      <div data-id="element-5388">
                        <div className="flex items-center gap-2 mb-1" data-id="element-5389">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight" data-id="element-5390">
                            {promotion.name}
                          </h3>
                          {getStatusBadge(status)}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1" data-id="element-5391">
                          {promotion.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-5392">
                      <button onClick={() => toggleActive(promotion)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title={promotion.active ? 'Desactivar' : 'Activar'} data-id="element-5393">
                        {promotion.active ? <ToggleRight className="text-green-600" size={24} data-id="element-5394" /> : <ToggleLeft size={24} data-id="element-5395" />}
                      </button>
                      <button onClick={() => {
                  setEditingPromotion(promotion);
                  setShowModal(true);
                }} className="p-2 text-gray-400 hover:text-blue-600 transition-colors" title="Editar" data-id="element-5396">
                        <Edit2 size={18} data-id="element-5397" />
                      </button>
                      <button onClick={() => setDeleteConfirm(promotion)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Eliminar" data-id="element-5398">
                        <Trash2 size={18} data-id="element-5399" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-4 space-y-3 border border-gray-100 dark:border-gray-800" data-id="element-5400">
                    <div className="flex items-center justify-between text-sm" data-id="element-5401">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-5402">
                        Descuento
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-5403">
                        {promotion.type === 'percentage' ? `${promotion.value}%` : `RD$ ${promotion.value}`}
                      </span>
                    </div>

                    {promotion.code && <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5404">
                        <span className="text-gray-500 dark:text-gray-400" data-id="element-5405">
                          Código
                        </span>
                        <code className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 rounded font-mono text-xs font-bold tracking-wide" data-id="element-5406">
                          {promotion.code}
                        </code>
                      </div>}

                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-5407">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-5408">
                        Vigencia
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium text-xs" data-id="element-5409">
                        {new Date(promotion.validFrom).toLocaleDateString()} -{' '}
                        {new Date(promotion.validTo).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Usage Progress */}
                  {promotion.usageLimit > 0 && <div className="mb-2" data-id="element-5410">
                      <div className="flex justify-between text-xs mb-1.5" data-id="element-5411">
                        <span className="text-gray-500" data-id="element-5412">
                          Uso: {promotion.usageCount} / {promotion.usageLimit}
                        </span>
                        <span className="font-bold text-gray-700 dark:text-gray-300" data-id="element-5413">
                          {Math.round(usagePercent)}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden" data-id="element-5414">
                        <div className={`h-full rounded-full transition-all duration-500 ${usagePercent >= 100 ? 'bg-red-500' : usagePercent >= 80 ? 'bg-orange-500' : 'bg-blue-500'}`} style={{
                  width: `${usagePercent}%`
                }} data-id="element-5415" />
                      </div>
                    </div>}

                  {/* Restrictions Badges */}
                  <div className="flex flex-wrap gap-2 mt-3" data-id="element-5416">
                    {promotion.applicableCompanies?.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 rounded text-[10px] font-medium border border-purple-100 dark:border-purple-800" data-id="element-5417">
                        <Building2 size={10} data-id="element-5418" />{' '}
                        {promotion.applicableCompanies.length} Empresas
                      </span>}
                    {promotion.applicableMenuItems?.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 rounded text-[10px] font-medium border border-orange-100 dark:border-orange-800" data-id="element-5419">
                        <Utensils size={10} data-id="element-5420" />{' '}
                        {promotion.applicableMenuItems.length} Platos
                      </span>}
                    {promotion.minOrderAmount > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-[10px] font-medium border border-gray-200 dark:border-gray-700" data-id="element-5421">
                        Min: RD$ {promotion.minOrderAmount}
                      </span>}
                  </div>
                </div>
              </Card>;
      })}
      </div>

      {/* Promotion Form Modal */}
      {showModal && <PromotionFormModal promotion={editingPromotion} companies={companies} menuItems={menuItems} onSave={handleSave} onClose={() => {
      setShowModal(false);
      setEditingPromotion(null);
    }} data-id="element-5422" />}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-5423">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-5424">
            <div className="p-6 text-center" data-id="element-5425">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-5426">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-5427" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-5428">
                ¿Eliminar promoción?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6" data-id="element-5429">
                ¿Estás seguro de que deseas eliminar{' '}
                <strong data-id="element-5430">"{deleteConfirm.name}"</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="flex gap-3" data-id="element-5431">
                <Button type="button" variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-5432">
                  Cancelar
                </Button>
                <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700 transition-colors" data-id="element-5433">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Promotion Form Modal Component
function PromotionFormModal({
  promotion,
  companies,
  menuItems,
  onSave,
  onClose
}: {
  promotion: Promotion | null;
  companies: any[];
  menuItems: any[];
  onSave: (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    name: promotion?.name || '',
    description: promotion?.description || '',
    type: (promotion?.type || 'percentage') as any,
    value: promotion?.value || 0,
    code: promotion?.code || '',
    minOrderAmount: promotion?.minOrderAmount || 0,
    maxDiscount: promotion?.maxDiscount || 0,
    validFrom: promotion?.validFrom || new Date().toISOString().split('T')[0],
    validTo: promotion?.validTo || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    active: promotion?.active ?? true,
    usageLimit: promotion?.usageLimit || 0,
    applicableCompanies: promotion?.applicableCompanies || [],
    applicableMenuItems: promotion?.applicableMenuItems || [],
    createdBy: promotion?.createdBy || 'Admin'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  const toggleCompany = (id: number) => {
    const current = formData.applicableCompanies;
    const updated = current.includes(id) ? current.filter(c => c !== id) : [...current, id];
    setFormData({
      ...formData,
      applicableCompanies: updated
    });
  };
  const toggleMenuItem = (id: string) => {
    const current = formData.applicableMenuItems;
    const updated = current.includes(id) ? current.filter(i => i !== id) : [...current, id];
    setFormData({
      ...formData,
      applicableMenuItems: updated
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-id="element-5434">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 dark:border-gray-800 my-8 flex flex-col max-h-[90vh]" data-id="element-5435">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-900 z-10 rounded-t-2xl" data-id="element-5436">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5437">
            {promotion ? 'Editar Promoción' : 'Nueva Promoción'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-5438">
            <span className="text-2xl leading-none" data-id="element-5439">&times;</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8 overflow-y-auto flex-1" data-id="element-5440">
          {/* Basic Info */}
          <section className="space-y-4" data-id="element-5441">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5442">
              Información Básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5443">
              <div className="md:col-span-2" data-id="element-5444">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5445">
                  Nombre de la Promoción
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Ej: Descuento de Verano" data-id="element-5446" />
              </div>

              <div className="md:col-span-2" data-id="element-5447">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5448">
                  Descripción
                </label>
                <textarea required rows={2} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" placeholder="Describe la promoción..." data-id="element-5449" />
              </div>

              <div data-id="element-5450">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5451">
                  Tipo de Promoción
                </label>
                <select value={formData.type} onChange={e => setFormData({
                ...formData,
                type: e.target.value as any
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5452">
                  <option value="percentage" data-id="element-5453">Porcentaje de Descuento</option>
                  <option value="fixed" data-id="element-5454">Monto Fijo</option>
                  <option value="bogo" data-id="element-5455">Compra 1 Lleva 2</option>
                  <option value="free_item" data-id="element-5456">Artículo Gratis</option>
                </select>
              </div>

              <div data-id="element-5457">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5458">
                  Valor {formData.type === 'percentage' ? '(%)' : '(RD$)'}
                </label>
                <input type="number" required min="0" step="0.01" value={formData.value} onChange={e => setFormData({
                ...formData,
                value: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5459" />
              </div>

              <div data-id="element-5460">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5461">
                  Código Promocional (Opcional)
                </label>
                <input type="text" value={formData.code} onChange={e => setFormData({
                ...formData,
                code: e.target.value.toUpperCase()
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono uppercase tracking-wide" placeholder="VERANO2024" data-id="element-5462" />
              </div>
            </div>
          </section>

          {/* Restrictions */}
          <section className="space-y-4" data-id="element-5463">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5464">
              Restricciones y Validez
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-5465">
              <div data-id="element-5466">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5467">
                  Pedido Mínimo (RD$)
                </label>
                <input type="number" min="0" step="0.01" value={formData.minOrderAmount} onChange={e => setFormData({
                ...formData,
                minOrderAmount: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5468" />
              </div>

              <div data-id="element-5469">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5470">
                  Descuento Máximo (RD$)
                </label>
                <input type="number" min="0" step="0.01" value={formData.maxDiscount} onChange={e => setFormData({
                ...formData,
                maxDiscount: parseFloat(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5471" />
              </div>

              <div data-id="element-5472">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5473">
                  Válido Desde
                </label>
                <input type="date" required value={formData.validFrom} onChange={e => setFormData({
                ...formData,
                validFrom: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5474" />
              </div>

              <div data-id="element-5475">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5476">
                  Válido Hasta
                </label>
                <input type="date" required value={formData.validTo} onChange={e => setFormData({
                ...formData,
                validTo: e.target.value
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5477" />
              </div>

              <div data-id="element-5478">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5" data-id="element-5479">
                  Límite de Usos (0 = ilimitado)
                </label>
                <input type="number" min="0" value={formData.usageLimit} onChange={e => setFormData({
                ...formData,
                usageLimit: parseInt(e.target.value)
              })} className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5480" />
              </div>
            </div>
          </section>

          {/* Applicability */}
          <section className="space-y-4" data-id="element-5481">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2" data-id="element-5482">
              Aplicabilidad
            </h3>

            <div data-id="element-5483">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5484">
                Empresas (Selecciona para restringir)
              </label>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-800/50" data-id="element-5485">
                {companies.length === 0 ? <p className="text-sm text-gray-500 text-center py-2" data-id="element-5486">
                    No hay empresas registradas
                  </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-5487">
                    {companies.map(company => <label key={company.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-5488">
                        <input type="checkbox" checked={formData.applicableCompanies.includes(company.id)} onChange={() => toggleCompany(company.id)} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-5489" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-5490">
                          {company.name}
                        </span>
                      </label>)}
                  </div>}
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5491">
                {formData.applicableCompanies.length === 0 ? 'Válido para TODAS las empresas' : `Restringido a ${formData.applicableCompanies.length} empresa(s)`}
              </p>
            </div>

            <div data-id="element-5492">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5493">
                Platos Específicos (Selecciona para restringir)
              </label>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-40 overflow-y-auto bg-gray-50 dark:bg-gray-800/50" data-id="element-5494">
                {menuItems.length === 0 ? <p className="text-sm text-gray-500 text-center py-2" data-id="element-5495">
                    No hay platos registrados
                  </p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-5496">
                    {menuItems.map(item => <label key={item.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-5497">
                        <input type="checkbox" checked={formData.applicableMenuItems.includes(item.id)} onChange={() => toggleMenuItem(item.id)} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-5498" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-5499">
                          {item.name}
                        </span>
                      </label>)}
                  </div>}
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5500">
                {formData.applicableMenuItems.length === 0 ? 'Válido para TODOS los platos' : `Restringido a ${formData.applicableMenuItems.length} plato(s)`}
              </p>
            </div>
          </section>
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl flex gap-3" data-id="element-5501">
          <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-5502">
            Cancelar
          </Button>
          <Button type="submit" fullWidth onClick={handleSubmit} data-id="element-5503">
            {promotion ? 'Actualizar' : 'Crear'} Promoción
          </Button>
        </div>
      </div>
    </div>;
}